import api from "./api.service";
import { getSelectedColoc } from "./coloc.service";

export interface AvailableColoc {
  id: string;
  name: string;
  address: string;
  zip_code: string;
  city: string;
}

export interface Log {
  id: string;
  fileName: string;
  colocId: string;
  status: "success" | "error" | "pending_assignment" | "assigned" | "dismissed";
  providerName?: string;
  amount?: number;
  errorMessage?: string;
  stackTrace?: string;
  createdAt: string;
  invoiceId?: string;
  context?: {
    senderEmail?: string;
    filePath?: string;
    availableColocs?: AvailableColoc[];
    [key: string]: any;
  };
}

export interface LogsResponse {
  data: Log[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class LogsService {
  async getLogs(page = 1, limit = 10, status?: "success" | "error"): Promise<LogsResponse> {
    let success: string | undefined;
    if (status === "success") {
      success = "true";
    } else if (status === "error") {
      success = "false";
    }

    const params: Record<string, string | number> = { page, limit };
    if (success !== undefined) {
      params.success = success;
    }

    const selectedColoc = getSelectedColoc();
    if (selectedColoc) {
      params.colocId = selectedColoc.id;
    }

    const response = await api.get("logs", { params });
    return response.data;
  }

  async getPendingAssignmentLogs(page = 1, limit = 10): Promise<LogsResponse> {
    const params: Record<string, string | number> = {
      page,
      limit,
      status: "pending_assignment" as any,
    };
    const response = await api.get("logs", { params });
    return response.data;
  }

  async getLogById(id: string): Promise<Log> {
    const response = await api.get(`logs/${id}`);
    return response.data;
  }

  async getFileAsDataUrl(colocId: string, fileName: string): Promise<string> {
    const response = await api.get(`files/${colocId}/${encodeURIComponent(fileName)}/data-url`);
    return response.data.dataUrl;
  }

  async getUnassignedFileAsDataUrl(fileName: string): Promise<string> {
    const response = await api.get(`files/_unassigned/${encodeURIComponent(fileName)}/data-url`);
    return response.data.dataUrl;
  }

  async validateInvoice(
    logId: string,
    providerId: string,
    amount: number,
    initialAmount: number,
    periodStartDate?: string,
    periodEndDate?: string,
    invoiceDate?: string,
    consumptionQuantity?: number,
    consumptionUnit?: string,
    consumptionEstimated?: boolean
  ): Promise<Log> {
    if (!logId) {
      throw new Error("Invalid log ID");
    }
    if (!providerId) {
      throw new Error("Provider ID is required");
    }
    // Négatif accepté : c'est un avoir (ticket #47). Nul, jamais.
    if (isNaN(amount) || amount === 0) {
      throw new Error("Le montant final doit être un nombre non nul");
    }
    if (isNaN(initialAmount) || initialAmount === 0) {
      throw new Error("Le montant initial doit être un nombre non nul");
    }

    const payload: Record<string, any> = {
      providerId,
      amount,
      initialAmount,
    };
    if (periodStartDate) payload.periodStartDate = periodStartDate;
    if (periodEndDate) payload.periodEndDate = periodEndDate;
    if (invoiceDate) payload.invoiceDate = invoiceDate;
    if (consumptionQuantity !== undefined && !isNaN(consumptionQuantity)) {
      payload.consumptionQuantity = consumptionQuantity;
      payload.consumptionUnit = consumptionUnit;
      if (consumptionEstimated !== undefined) payload.consumptionEstimated = consumptionEstimated;
    }

    const response = await api.post(`logs/${logId}/validate`, payload);
    return response.data;
  }

  async assignColoc(logId: string, colocId: string): Promise<Log> {
    if (!logId) {
      throw new Error("Invalid log ID");
    }
    if (!colocId) {
      throw new Error("Coloc ID is required");
    }

    const response = await api.post(`logs/${logId}/assign-coloc`, {
      colocId,
    });
    return response.data;
  }

  async retryLog(logId: string): Promise<{ success: boolean; message: string }> {
    if (!logId) {
      throw new Error("Invalid log ID");
    }

    const response = await api.post(`logs/${logId}/retry`);
    return response.data;
  }

  async dismissLog(logId: string): Promise<Log> {
    if (!logId) {
      throw new Error("Invalid log ID");
    }

    const response = await api.post(`logs/${logId}/dismiss`);
    return response.data;
  }
}

export const logsService = new LogsService();
